import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Input } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/misc'
import { ISO8601_DATETIME_FORMATS } from 'utils/constants'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import { allowEnterPress } from '../utils'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'

const getLocalTimeFormat = (selectedLocale) => {
  const localeKey = selectedLocale || 'en'
  return moment().locale(localeKey).localeData()._longDateFormat.LT
}

const buildTimeAnswer = ({ label, id, selectedLocale, time }) => {
  if (!time) return []
  const momentTime = moment(time, moment.HTML5_FMT.TIME, true)
  if (!momentTime.isValid()) return []
  const localTimeFormat = getLocalTimeFormat(selectedLocale)
  return [
    {
      label,
      value: `${momentTime.format(localTimeFormat)}, ${localTimeFormat}, ${momentTime.format(
        ISO8601_DATETIME_FORMATS.time,
      )}`,
      question: id,
    },
  ]
}

const completeTimeQuestion = (props, time) => {
  const {
    completeQuestion,
    id,
    nextQ,
    nextQId,
    question,
    selectedLocale,
    submissionSurvey,
    webSurvey,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props

  const localTimeFormat = getLocalTimeFormat(selectedLocale)

  const { label, type, attributes } = question
  const { required } = attributes
  const answer = buildTimeAnswer({ label, id, selectedLocale, time })
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: time ? 'ANSWERED' : 'SKIPPED',
    answer
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
}

function TimeType(props) {
  const [time, setTime] = useState('')

  const {
    className,
    currentSurvey,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    setAllRequiredAnswered,
    strings,
    storedAnswer,
    forwardedRef,
    submissionLoading,
    submissionSurvey,
    webSurvey,
    isSibling = false,
  } = props
  const { label, attributes } = question
  const { required } = attributes
  const _className = className ? ` ${className}` : ''

  useEffect(() => {
    if (storedAnswer?.[0]?.value) {
      const value = storedAnswer?.[0]?.value
      const parts = String(value)
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
      const isoTime = parts[2] // stored as HH:mm
      const parsed = isoTime
        ? moment(isoTime, ISO8601_DATETIME_FORMATS.time, true)
        : moment.invalid()
      const hhmm = parsed.isValid() ? parsed.format('HH:mm') : ''
      if (hhmm) onInput(hhmm)
      // Complete immediately with the stored value
      if (!props.suppressAutoAdvance) {
        if (hhmm) completeTimeQuestion(props, hhmm)
      }
    }
  }, [])

  const onInput = (value) => {
    setAllRequiredAnswered(false, id)
    setTime(value)
    // Autosave into Redux so sibling pages see answers and can enable Next
    // (per-question footer is hidden when isSibling).
    try {
      const { question, webSurvey, completeQuestion, selectedLocale } = props
      const { label, type, attributes } = question
      const { required } = attributes
      const answer = buildTimeAnswer({ label, id, selectedLocale, time: value })
      const submission = {
        id,
        label,
        name: id,
        type,
        required,
        state: value ? 'ANSWERED' : 'SKIPPED',
        answer,
      }
      if (typeof completeQuestion === 'function' && webSurvey) {
        completeQuestion(submission, webSurvey.order?.[0])
      }
    } catch (e) {
      console.warn('TimeType autosave failed', { error: e })
    }
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div ref={forwardedRef} className={`survey-question time${_className}`}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label)
              }}
            />
          </p>
          <Input
            type='time'
            onChange={(val) => onInput(val)}
            inputProps={{
              onKeyPress: (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (allowEnterPress(required, time)) {
                    completeTimeQuestion(props, time)
                  }
                }
              }
            }}
            value={time}
          />
          {!isSibling && (
            <QuestionFooter
              completed={!!time}
              id={id}
              isConsent={isConsent}
              isLastQuestion={isLastQuestion}
              loading={submissionLoading}
              onComplete={() => completeTimeQuestion(props, time)}
              question={question}
              renderBackButton={renderBackButton}
              strings={strings}
              submissionSurvey={submissionSurvey}
              webSurvey={webSurvey}
            />
          )}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
}

const ForwardedTimeType = React.forwardRef((props, ref) => (
  <TimeType {...props} forwardedRef={ref} />
))

export default ForwardedTimeType

TimeType.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ])
}
